import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { programService } from '../../../services/programService';
import { admissionService } from '../../../services/admissionService';

const ProgramDetailsModal = ({ isOpen, onClose, program, onEdit }) => {
  const [fullProgramDetails, setFullProgramDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [admissionLevels, setAdmissionLevels] = useState([]);
  const [savingLevel, setSavingLevel] = useState(null);

  useEffect(() => {
    if (isOpen && program?.id) {
      fetchProgramDetails();
    }
  }, [isOpen, program?.id]);

  const fetchProgramDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const [details, configuredLevels] = await Promise.all([
        programService?.getById(program?.id),
        admissionService.getLevelConfigurations(program?.id)
      ]);
      setFullProgramDetails(details);
      const offeredLevels = (details?.levels || []).map(level => Number(String(level).match(/[3-7]/)?.[0])).filter(Boolean);
      setAdmissionLevels(offeredLevels.map(level => configuredLevels.find(item => Number(item.level) === level) || {
        program_id: program.id, level, capacity: 0, kuccps_capacity: 0, self_sponsored_capacity: 0,
        is_active: false, program_level_code: '', minimum_mean_grade: '', admission_criteria: {}
      }));
    } catch (err) {
      console.error('Error fetching program details:', err);
      setError(err?.message || 'Failed to load program details');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const displayProgram = fullProgramDetails || program;

  if (!displayProgram) return null;

  const getLevelColor = (level) => {
    // Handle both single level string and multi-level array
    const levelValue = Array.isArray(level) ? level?.[0] : level;

    switch (levelValue) {
      case 'level_6_diploma': case 'Diploma':
        return 'bg-primary text-primary-foreground';
      case 'level_5_craft': case 'Craft':
        return 'bg-accent text-accent-foreground';
      case 'level_4_artisan': case 'Artisan':
        return 'bg-secondary text-secondary-foreground';
      case 'level_3': case 'NVC':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getLevelLabel = (levels) => {
    if (Array.isArray(levels) && levels?.length > 0) {
      const levelMap = {
        'level_3': 'Level 3 - NVC',
        'level_4_artisan': 'Level 4 - Artisan',
        'level_5_craft': 'Level 5 - Craft',
        'level_6_diploma': 'Level 6 - Diploma'
      };
      return levels?.map(l => levelMap?.[l] || l)?.join(', ');
    }
    return levels || 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-success text-success-foreground';
      case 'Inactive':
        return 'bg-error text-error-foreground';
      case 'Draft':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const utilizationRate = displayProgram?.capacity > 0
    ? Math.round((displayProgram?.enrolledStudents / displayProgram?.capacity) * 100)
    : 0;

  const updateAdmissionLevel = (level, changes) => setAdmissionLevels(current => current.map(item => item.level === level ? { ...item, ...changes } : item));
  const updateCriteria = (level, key, value) => setAdmissionLevels(current => current.map(item => item.level === level ? { ...item, admission_criteria: { ...(item.admission_criteria || {}), [key]: value } } : item));
  const saveAdmissionLevel = async configuration => {
    try {
      setSavingLevel(configuration.level); setError(null);
      const kuccpsCapacity=Number(configuration.kuccps_capacity)||0, selfCapacity=Number(configuration.self_sponsored_capacity)||0;
      const saved=await admissionService.saveLevelConfiguration({ ...configuration, capacity:kuccpsCapacity+selfCapacity, kuccps_capacity:kuccpsCapacity, self_sponsored_capacity:selfCapacity, minimum_mean_grade:configuration.minimum_mean_grade||null });
      setAdmissionLevels(current=>current.map(item=>item.level===saved.level?saved:item));
    } catch(err) { setError(err?.message||'Unable to save admission requirements.'); }
    finally { setSavingLevel(null); }
  };

  // No longer using the legacy core/common/basic logic

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2 flex-wrap">
                <h2 className="text-2xl font-bold text-foreground">{displayProgram?.name}</h2>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getLevelColor(displayProgram?.levels || displayProgram?.level)}`}>
                  {getLevelLabel(displayProgram?.levels || displayProgram?.level)}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(displayProgram?.status)}`}>
                  {displayProgram?.isActive ? 'Active' : displayProgram?.status || 'Inactive'}
                </span>
              </div>
              <p className="text-lg text-muted-foreground">{displayProgram?.code}</p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader" size={32} className="animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Loading program details...</span>
            </div>
          )}

          {error && (
            <div className="bg-error/10 border border-error rounded-lg p-4 text-center">
              <Icon name="AlertCircle" size={24} className="mx-auto text-error mb-2" />
              <p className="text-error">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchProgramDetails} className="mt-3">
                <Icon name="RefreshCw" size={14} className="mr-1" />
                Retry
              </Button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="Users" size={20} className="text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {displayProgram?.enrolledStudents ?? 0}/{displayProgram?.capacity ?? 100}
                  </div>
                  <div className="text-sm text-muted-foreground">Students (Enrolled/Capacity)</div>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="Briefcase" size={20} className="text-accent" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">{displayProgram?.capacity || 100}</div>
                  <div className="text-sm text-muted-foreground">Total Capacity</div>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="TrendingUp" size={20} className="text-success" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">{utilizationRate}%</div>
                  <div className="text-sm text-muted-foreground">Utilization</div>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="BookOpen" size={20} className="text-secondary" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">{displayProgram?.totalUnits ?? displayProgram?.units?.length ?? 0}</div>
                  <div className="text-sm text-muted-foreground">Total Units</div>
                </div>
              </div>

              {/* Program Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Program Information</h3>
                <div className="bg-muted rounded-lg p-6 space-y-4">
                  {/* Level Breakdown Table */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-foreground mb-3">Level Configuration</h4>
                    <div className="bg-background rounded-md border border-border overflow-hidden">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-muted/50 text-muted-foreground font-medium">
                          <tr>
                            <th className="px-4 py-2 border-b border-border">Level</th>
                            <th className="px-4 py-2 border-b border-border">Modules</th>
                            <th className="px-4 py-2 border-b border-border">Capacity</th>
                            <th className="px-4 py-2 border-b border-border">Enrolled</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {(displayProgram?.levels || []).map((level) => {
                            const levelVal = Array.isArray(level) ? level[0] : level;
                            // Map DB level name to simple name if needed, or use existing map
                            const levelMap = {
                              'level_3': 'Level 3 (NVC)',
                              'level_4_artisan': 'Level 4 (Artisan)',
                              'level_5_craft': 'Level 5 (Craft)',
                              'level_6_diploma': 'Level 6 (Diploma)',
                              '3': 'Level 3 (NVC)',
                              '4': 'Level 4 (Artisan)',
                              '5': 'Level 5 (Craft)',
                              '6': 'Level 6 (Diploma)'
                            };
                            const levelKey = Object.keys(levelMap).find(k => levelMap[k] === levelVal) || String(levelVal).replace('level_', '').replace('_', ' ');
                            // Try to find matching key in durations/capacities (which use '3', '4' etc in form but maybe full keys in DB?
                            // Form saved keys as '3', '4'. DB might have them as such if saved as JSON.
                            // Let's assume keys match what is in levelDurations (which comes from DB JSON).
                            // We need to normalize keys.
                            // The level values in `levels` array are like 'level_6_diploma'.
                            // The keys in `levelDurations` are like '6'.
                            const shortKey = levelVal.includes('diploma') ? '6' : levelVal.includes('craft') ? '5' : levelVal.includes('artisan') ? '4' : levelVal.includes('level_3') ? '3' : levelVal;

                            return (
                              <tr key={levelVal}>
                                <td className="px-4 py-2">{levelMap[levelVal] || levelVal}</td>
                                <td className="px-4 py-2">{displayProgram?.levelDurations?.[shortKey] || 'N/A'}</td>
                                <td className="px-4 py-2">{displayProgram?.levelCapacities?.[shortKey] || 'N/A'}</td>
                                <td className="px-4 py-2">{displayProgram?.enrolledByLevel?.[shortKey] || displayProgram?.enrolledByLevel?.[levelVal] || 0}</td>
                              </tr>
                            );
                          })}
                          {(displayProgram?.levels || []).length === 0 && (
                            <tr>
                              <td colSpan="4" className="px-4 py-2 text-center text-muted-foreground">No level configuration found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Department</label>
                      <p className="text-foreground font-medium">{displayProgram?.department?.name || displayProgram?.department || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Faculty</label>
                      <p className="text-foreground font-medium">{displayProgram?.department?.faculty?.name || 'N/A'}</p>
                    </div>
                    {/* Duration moved to breakdown table */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Program Code</label>
                      <p className="text-foreground font-medium">{displayProgram?.code}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                      <p className="text-foreground font-medium">{displayProgram?.isActive ? 'Active' : 'Inactive'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created Date</label>
                      <p className="text-foreground font-medium">
                        {displayProgram?.createdAt ? new Date(displayProgram.createdAt)?.toLocaleDateString() :
                          displayProgram?.created_at ? new Date(displayProgram.created_at)?.toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div><h3 className="text-lg font-semibold text-foreground">Admission requirements by level</h3><p className="text-sm text-muted-foreground mt-1">These are the same requirements and per-intake allocations shown in the admissions portal.</p></div>
                {admissionLevels.length === 0 ? <div className="rounded-lg border border-border bg-muted p-5 text-sm text-muted-foreground">Add programme levels in Edit Program before configuring admission requirements.</div> : admissionLevels.map(configuration => <div key={configuration.level} className="rounded-lg border border-border bg-muted/30 p-5 space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3"><h4 className="font-semibold">Level {configuration.level}</h4><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(configuration.is_active)} onChange={event=>updateAdmissionLevel(configuration.level,{is_active:event.target.checked})}/>Open for admission</label></div>
                  <div className="grid md:grid-cols-4 gap-4">
                    <label className="text-sm">Programme level code<input className="mt-1 w-full rounded-md border bg-muted px-3 py-2 uppercase text-muted-foreground" value={configuration.program_level_code||''} readOnly placeholder="Configured in Edit Programme"/></label>
                    <label className="text-sm">KUCCPS places per intake<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="number" min="0" value={configuration.kuccps_capacity||0} onChange={event=>updateAdmissionLevel(configuration.level,{kuccps_capacity:event.target.value})}/></label>
                    <label className="text-sm">Self-sponsored places per intake<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="number" min="0" value={configuration.self_sponsored_capacity||0} onChange={event=>updateAdmissionLevel(configuration.level,{self_sponsored_capacity:event.target.value})}/></label>
                    <label className="text-sm">Minimum mean grade<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" value={configuration.minimum_mean_grade||''} onChange={event=>updateAdmissionLevel(configuration.level,{minimum_mean_grade:event.target.value})} placeholder="e.g. C-"/></label>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <label className="text-sm">Required qualification<textarea className="mt-1 w-full min-h-20 rounded-md border bg-background px-3 py-2" value={configuration.admission_criteria?.qualification||''} onChange={event=>updateCriteria(configuration.level,'qualification',event.target.value)} placeholder="e.g. KCSE or Level 5 certificate"/></label>
                    <label className="text-sm">General entry requirement<textarea className="mt-1 w-full min-h-20 rounded-md border bg-background px-3 py-2" value={configuration.admission_criteria?.description||''} onChange={event=>updateCriteria(configuration.level,'description',event.target.value)} placeholder="Requirement shown to applicants"/></label>
                    <label className="text-sm">Subject requirements<textarea className="mt-1 w-full min-h-20 rounded-md border bg-background px-3 py-2" value={configuration.admission_criteria?.subject_requirements||''} onChange={event=>updateCriteria(configuration.level,'subject_requirements',event.target.value)}/></label>
                    <label className="text-sm">Alternative qualifications<textarea className="mt-1 w-full min-h-20 rounded-md border bg-background px-3 py-2" value={configuration.admission_criteria?.alternative_qualifications||''} onChange={event=>updateCriteria(configuration.level,'alternative_qualifications',event.target.value)}/></label>
                    <label className="text-sm md:col-span-2">Required supporting documents<textarea className="mt-1 w-full min-h-16 rounded-md border bg-background px-3 py-2" value={configuration.admission_criteria?.required_documents||''} onChange={event=>updateCriteria(configuration.level,'required_documents',event.target.value)} placeholder="Result slip, certificate, transcript…"/></label>
                  </div>
                  <div className="flex justify-end"><Button size="sm" onClick={()=>saveAdmissionLevel(configuration)} disabled={savingLevel===configuration.level}>{savingLevel===configuration.level?'Saving…':`Save Level ${configuration.level} requirements`}</Button></div>
                </div>)}
              </div>

              {/* Unit Breakdown by Level */}
              {displayProgram?.levels && displayProgram.levels.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Unit Breakdown by Level</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {displayProgram.levels.map((level) => {
                      const levelNumMatch = level.match(/\d+/);
                      const levelNum = levelNumMatch ? parseInt(levelNumMatch[0]) : null;
                      const maxModule = parseInt(displayProgram.levelDurations?.[levelNum] || 0);

                      // Count units in modules 1 to maxModule
                      const count = (displayProgram.units || []).filter(u =>
                        (u.module || 1) <= maxModule
                      ).length;

                      const levelMap = {
                        'level_3': 'Level 3 - NVC',
                        'level_4_artisan': 'Level 4 - Artisan',
                        'level_5_craft': 'Level 5 - Craft',
                        'level_6_diploma': 'Level 6 - Diploma'
                      };

                      return (
                        <div key={level} className="bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
                          <div className="text-3xl font-bold text-primary mb-2">{count}</div>
                          <div className="text-sm font-medium text-foreground">{levelMap[level] || level}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Modules 1 to {maxModule}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-muted/50 rounded-lg p-4 mt-4">
                    <p className="text-sm text-muted-foreground">
                      <Icon name="Info" size={16} className="inline mr-2" />
                      Unit counts represent all units from <strong>Module 1</strong> up to the <strong>maximum module</strong> defined for that level.
                    </p>
                  </div>
                </div>
              )}

              {/* Units List - Grouped by Module with Filtering */}
              {displayProgram?.units && displayProgram?.units?.length > 0 && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h3 className="text-lg font-semibold text-foreground">Program Units List</h3>
                    <div className="flex items-center space-x-2">
                      <select
                        value={moduleFilter}
                        onChange={(e) => setModuleFilter(e.target.value)}
                        className="text-xs px-2 py-1 rounded border bg-background"
                      >
                        <option value="all">All Modules</option>
                        {[...new Set(displayProgram.units.map(u => u.module || 1))].sort((a, b) => a - b).map(m => (
                          <option key={m} value={m}>Module {m}</option>
                        ))}
                      </select>
                      <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="text-xs px-2 py-1 rounded border bg-background"
                      >
                        <option value="all">All Types</option>
                        <option value="Core">Core</option>
                        <option value="Common">Common</option>
                        <option value="Basic">Basic</option>
                      </select>
                    </div>
                  </div>

                  {/* Grouped Display */}
                  {[...new Set(displayProgram.units
                    .filter(u => (moduleFilter === 'all' || String(u.module || 1) === String(moduleFilter)))
                    .filter(u => (typeFilter === 'all' || String(u.unit_type || u.type || 'Core').toLowerCase() === typeFilter.toLowerCase()))
                    .map(u => u.module || 1)
                  )].sort((a, b) => a - b).map(moduleNum => (
                    <div key={moduleNum} className="space-y-3">
                      <h4 className="text-md font-medium text-primary border-b border-border pb-2">Module {moduleNum}</h4>
                      <div className="bg-muted rounded-lg divide-y divide-border">
                        {displayProgram.units
                          .filter(u => (u.module || 1) === moduleNum)
                          .filter(u => (typeFilter === 'all' || String(u.unit_type || u.type || 'Core').toLowerCase() === typeFilter.toLowerCase()))
                          .map((unit, index) => (
                            <div key={unit?.id || index} className="p-4 hover:bg-muted/50 transition-colors">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <p className="font-medium text-foreground">{unit?.name}</p>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border bg-background uppercase text-muted-foreground font-bold">
                                      {unit?.unit_type || unit?.type || 'Core'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{unit?.code}</p>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-foreground">{unit?.creditHours || unit?.credit_hours || unit?.credits} Credits</p>
                                    <p className={`text-xs ${unit?.isActive !== false ? 'text-success' : 'text-error'}`}>
                                      {unit?.isActive !== false ? 'Active' : 'Inactive'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                  {/* Empty state when filtering */}
                  {displayProgram.units.filter(u =>
                    (moduleFilter === 'all' || String(u.module || 1) === String(moduleFilter)) &&
                    (typeFilter === 'all' || String(u.unit_type || u.type || 'Core').toLowerCase() === typeFilter.toLowerCase())
                  ).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-lg border border-dashed">
                        No units matching the selected filters.
                      </div>
                    )}
                </div>
              )}

              {/* Enrollment Progress */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Enrollment Progress</h3>
                <div className="bg-muted rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">
                      {displayProgram?.enrolledStudents ?? 0} / {displayProgram?.capacity ?? 100} Students
                    </span>
                    <span className="text-sm font-medium text-primary">{utilizationRate}%</span>
                  </div>
                  <div className="w-full bg-background rounded-full h-3">
                    <div
                      className="bg-primary h-3 rounded-full transition-all"
                      style={{ width: `${utilizationRate}%` }}
                    ></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-success">{displayProgram?.enrolledStudents ?? 0}</div>
                      <div className="text-xs text-muted-foreground">Enrolled</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-warning">
                        {Math.max(0, (displayProgram?.capacity ?? 100) - (displayProgram?.enrolledStudents ?? 0))}
                      </div>
                      <div className="text-xs text-muted-foreground">Available</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground">{displayProgram?.capacity ?? 100}</div>
                      <div className="text-xs text-muted-foreground">Total Capacity</div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6">
          <div className="flex items-center justify-end space-x-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="default"
              onClick={() => {
                onEdit(displayProgram);
              }}
              disabled={loading}
            >
              <Icon name="Edit" size={16} className="mr-2" />
              Edit Program
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramDetailsModal;
